import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Megaphone, Mail, MessageSquare, Share2 } from "lucide-react";
import { toast } from "sonner";

const CH_ICONS = { email: Mail, whatsapp: MessageSquare, sms: MessageSquare, social: Share2, multi: Megaphone };

export default function Marketing() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "email", business_unit: "FigureCX", audience: "", subject: "", body: "", status: "draft" });

  const load = () => api.get("/campaigns").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/campaigns", form);
    toast.success("Campaign created");
    setOpen(false);
    setForm({ name: "", channel: "email", business_unit: "FigureCX", audience: "", subject: "", body: "", status: "draft" });
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Marketing"
        subtitle="Multi-channel campaigns across email, WhatsApp, SMS and social"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-campaign-btn"><Plus className="w-4 h-4 mr-1" />New Campaign</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>New campaign</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="campaign-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Channel</Label>
                    <Select value={form.channel} onValueChange={v => setForm({...form, channel: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                        <SelectItem value="social">Social</SelectItem>
                        <SelectItem value="multi">Multi-channel</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Business Unit</Label>
                    <Select value={form.business_unit} onValueChange={v => setForm({...form, business_unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                        <SelectItem value="FigureCX">FigureCX</SelectItem>
                        <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <div><Label>Audience</Label><Input value={form.audience} onChange={e => setForm({...form, audience: e.target.value})} placeholder="e.g. All FigureCX customers" /></div>
                <div><Label>Subject / hook</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} /></div>
                <div><Label>Body</Label><Textarea rows={4} value={form.body} onChange={e => setForm({...form, body: e.target.value})} /></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-campaign-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map(c => {
          const Icon = CH_ICONS[c.channel] || Megaphone;
          const m = c.metrics || {};
          const openRate = m.delivered ? Math.round((m.opened / m.delivered) * 100) : 0;
          const clickRate = m.opened ? Math.round((m.clicked / m.opened) * 100) : 0;
          const convRate = m.clicked ? Math.round((m.converted / m.clicked) * 100) : 0;
          return (
            <div key={c.id} className="kpi-card" data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-brand-aqua-light text-brand-aqua-hover flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-800 dark:text-slate-100">{c.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 capitalize">{c.channel} · {c.business_unit}</div>
                  </div>
                </div>
                <Chip variant={statusChip(c.status)}>{c.status}</Chip>
              </div>
              {c.audience && <div className="text-xs text-slate-600 dark:text-slate-400 mb-3">Audience: {c.audience}</div>}
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Sent</div><div className="font-heading font-semibold text-slate-900 dark:text-slate-100 num">{m.sent || 0}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Open rate</div><div className="font-heading font-semibold text-emerald-600 num">{openRate}%</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Convert</div><div className="font-heading font-semibold text-brand-aqua-hover num">{convRate}%</div></div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 text-sm">No campaigns yet.</div>}
      </div>
    </div>
  );
}
